/**
 * DashboardPage cliente — a tela de Início do Portal (B1.6 §6, redesenhada na
 * FASE-C).
 *
 * O que mudou e por quê (autonomia de layout):
 *   - **Cabeçalho canônico**: o h1 agora é o nome do condomínio via `PortalPage`
 *     e existe em TODOS os estados (antes sumia enquanto carregava).
 *   - **Ação primeiro**: "Abrir chamado" é o que o síndico vem fazer aqui. Saiu
 *     do fim da tela (dentro de um card "Atalhos rápidos", nome de menu de
 *     software) e virou a ação do cabeçalho + o primeiro cartão do corpo. No
 *     375px o botão ocupa a largura toda — alvo de toque confortável.
 *   - **KPI canônico**: os três números usam o `KpiCard` da FASE-B (mesma
 *     altura, SF Mono tabular, rótulo 12px caps) em vez de um card local que
 *     divergia do resto do sistema. Cada um ganhou uma frase de contexto —
 *     número sem contexto não informa nada.
 *   - **Rótulos humanos**: "Chamados abertos (cliente)" virou "Chamados do
 *     condomínio"; parênteses com nome de entidade interna saíram.
 *
 * Permissões continuam resolvidas server-side (`dashboard.permissoes`); o guard
 * de UI é só camada extra. Trocar de cliente segue recarregando os dados.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Alert, Button, EmptyState, KpiCard } from '@/components/ui';
import { PortalPage } from '@/components/cliente/PortalPage';
import { PesquisaNoPortal } from '@/components/cliente/PesquisaNoPortal';
import { useCliente } from '@/contexts/ClienteContext';
import { useClienteDashboard } from '@/lib/cliente-portal-store';
import { mensagemErro } from '@/lib/mensagem-publica';

export function DashboardPage() {
  const { clienteAtivo } = useCliente();
  const { data, isLoading, isError, error } = useClienteDashboard();

  const permissoes = data?.permissoes;
  const podeAbrirChamado = permissoes?.abrir_chamado ?? false;

  return (
    <PortalPage
      testId="cliente-dashboard-page"
      crumb="Portal IGM"
      title={clienteAtivo?.nome ?? 'Portal IGM Security'}
      subtitle={
        clienteAtivo
          ? `Você está como ${clienteAtivo.role === 'gestor' ? 'gestor' : 'portaria'}.`
          : undefined
      }
      actions={
        podeAbrirChamado ? (
          <Link to="/cliente/chamado/novo" className="w-full sm:w-auto">
            <Button variant="primary" fullWidth data-testid="atalho-abrir-chamado">
              Abrir chamado
            </Button>
          </Link>
        ) : undefined
      }
    >
      {!clienteAtivo ? (
        <EmptyState
          title="Nenhum condomínio vinculado à sua conta"
          description="Assim que a IGM vincular você a um condomínio, tudo aparece por aqui. Se acha que isso é um engano, fale com a gente."
        />
      ) : isLoading ? (
        <PainelEsqueleto />
      ) : isError || !data ? (
        <Alert variant="danger">
          {mensagemErro(error, 'Não conseguimos carregar seu painel agora. Tente atualizar a página.')}
        </Alert>
      ) : (
        <div className="space-y-8">
          {/* D29 (mig 796): a pesquisa de satisfação pendente, quando houver. */}
          <PesquisaNoPortal />
          <section
            aria-label="Resumo"
            className="grid grid-cols-1 gap-4 sm:grid-cols-3"
            data-testid="kpis-grid"
          >
            <Link to="/cliente/meus-chamados" className="block focus:outline-none">
              <KpiCard
                label="Meus chamados"
                value={data.kpis.meusChamados}
                context="abertos por você"
                data-testid="kpi-meus-chamados"
                className="h-full transition hover:shadow-card-hover"
              />
            </Link>
            {permissoes?.ver_todos_chamados && (
              <Link to="/cliente/chamados" className="block focus:outline-none">
                <KpiCard
                  label="Chamados do condomínio"
                  value={data.kpis.chamadosAbertos}
                  context="abertos por toda a equipe"
                  data-testid="kpi-chamados-todos"
                  className="h-full transition hover:shadow-card-hover"
                />
              </Link>
            )}
            {permissoes?.ver_contratos && (
              <KpiCard
                label="Contratos ativos"
                value={data.kpis.contratosAtivos}
                context={<Link to="/cliente/contratos">ver contratos</Link>}
                data-testid="kpi-contratos"
                className="h-full"
              />
            )}
          </section>

          <section aria-labelledby="portal-o-que-fazer">
            <h2
              id="portal-o-que-fazer"
              className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3"
            >
              O que você pode fazer
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {podeAbrirChamado && (
                <CartaoAcao
                  to="/cliente/chamado/novo"
                  titulo="Abrir um chamado"
                  descricao="Câmera sem imagem, portão travado, alarme disparando — descreva o problema e a equipe é avisada na hora."
                />
              )}
              <CartaoAcao
                to="/cliente/meus-chamados"
                titulo="Acompanhar meus chamados"
                descricao="Veja em que pé está cada chamado que você abriu e converse com a equipe por lá."
                testId="atalho-meus-chamados"
              />
              {permissoes?.ver_todos_chamados && (
                <CartaoAcao
                  to="/cliente/chamados"
                  titulo="Ver os chamados do condomínio"
                  descricao="Tudo que a portaria e os outros gestores abriram, com filtro por período e situação."
                  testId="atalho-todos-chamados"
                />
              )}
              {permissoes?.ver_manutencoes_agendadas && (
                <CartaoAcao
                  to="/cliente/manutencoes"
                  titulo="Conferir as preventivas"
                  descricao="Quando é a próxima visita e o que já foi feito nas anteriores."
                />
              )}
            </div>
          </section>
        </div>
      )}
    </PortalPage>
  );
}

function CartaoAcao({
  to,
  titulo,
  descricao,
  testId,
}: {
  to: string;
  titulo: string;
  descricao: ReactNode;
  testId?: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col gap-1 rounded-xl border border-line bg-paper-surface p-4 transition hover:border-red/40 hover:shadow-card-hover dark:border-line-dark dark:bg-paper-dark-surface"
      data-testid={testId}
    >
      <span className="font-semibold text-ink-1 group-hover:text-red-deep dark:text-ink-dark-1 dark:group-hover:text-red-glow">
        {titulo}
      </span>
      <span className="text-sm leading-relaxed text-ink-2 dark:text-ink-dark-2">{descricao}</span>
    </Link>
  );
}

/** Esqueleto com a MESMA anatomia do painel — sem salto quando o dado chega. */
function PainelEsqueleto() {
  return (
    <div className="space-y-8" aria-busy="true" aria-live="polite">
      <span className="sr-only">Carregando seu painel…</span>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="min-h-[118px] animate-pulse rounded-xl border border-line bg-paper-surface dark:border-line-dark dark:bg-paper-dark-surface"
          />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-xl border border-line bg-paper-surface dark:border-line-dark dark:bg-paper-dark-surface"
          />
        ))}
      </div>
    </div>
  );
}
